import LikeFilled from '@ant-design/icons/LikeFilled'
import LikeOutlined from '@ant-design/icons/LikeOutlined'
import { ColumnDef } from '@tanstack/react-table'
import { MiddleTruncatedText } from 'features/knowledgeDocumentViewer/components/MiddleTruncatedText'
import { toTitleCase } from 'features/knowledgeDocumentViewer/utils/textHelpers'
import React from 'react'
import styled from 'styled-components'
import { InputType, SystemDocumentEntry } from 'types'

const RankCell = styled.span`
  display: flex;
  justify-content: center;
`

const FeedbackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  color: ${(props) => props.theme.forecast.secondaryText};
  transition: color 0.2s ease;

  &:hover {
    color: ${(props) => props.theme.forecast.primary};
  }

  &[data-selected='true'] {
    color: ${(props) => props.theme.forecast.primary};
  }
`

export type DocumentTableRow = {
  id: string
  title: string
  filename: string
  documentTitle: string | null
  system: string | null
  vendor: string | null
  docType: string | null
  drawing: string | null
  timestamp: string | null
  numPages: number | null
  revisionCode: string | null
  revisionDate: string | null
}

export const DocumentTableColumns = (): ColumnDef<DocumentTableRow>[] => [
  {
    id: 'title',
    accessorKey: 'title',
    header: 'File Name',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'includesString',
    cell: ({ row }) => {
      const titleCasedTitle = toTitleCase(row.original.title)
      return (
        <MiddleTruncatedText
          text={titleCasedTitle}
          maxWidth='100%'
          fillWidth
          tooltipFontSize='0.75rem'
        />
      )
    },
    meta: {
      inputType: InputType.Text,
    },
  },
  {
    id: 'documentTitle',
    accessorKey: 'documentTitle',
    header: 'Document Title',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'includesString',
    cell: ({ row }) => {
      const value = row.original.documentTitle || '-'
      const titleCasedValue = value === '-' ? value : toTitleCase(value)
      return (
        <MiddleTruncatedText
          text={titleCasedValue}
          maxWidth='100%'
          fillWidth
          tooltipFontSize='0.75rem'
        />
      )
    },
    meta: {
      inputType: InputType.Text,
    },
  },
  {
    id: 'numPages',
    accessorKey: 'numPages',
    header: 'Length',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      return <span>{row.original.numPages ?? 1}</span>
    },
  },
  {
    id: 'system',
    accessorKey: 'system',
    header: 'System',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'includesString',
    cell: ({ row }) => {
      return <span>{row.original.system || '-'}</span>
    },
    meta: {
      inputType: InputType.Text,
    },
  },
  {
    id: 'vendor',
    accessorKey: 'vendor',
    header: 'Vendor',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'includesString',
    cell: ({ row }) => {
      return <span>{row.original.vendor || '-'}</span>
    },
    meta: {
      inputType: InputType.Text,
    },
  },
  {
    id: 'docType',
    accessorKey: 'docType',
    header: 'Type',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'includesString',
    minSize: 120,
    size: 150,
    cell: ({ row }) => {
      return <span>{row.original.docType || '-'}</span>
    },
    meta: {
      inputType: InputType.Text,
    },
  },
  {
    id: 'drawing',
    accessorKey: 'drawing',
    header: 'Drawing Number',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'includesString',
    cell: ({ row }) => {
      return <span>{row.original.drawing || '-'}</span>
    },
    meta: {
      inputType: InputType.Text,
    },
  },
  {
    id: 'revisionCode',
    accessorKey: 'revisionCode',
    header: 'Rev',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      return <span>{row.original.revisionCode || '-'}</span>
    },
  },
  {
    id: 'revisionDate',
    accessorKey: 'revisionDate',
    header: 'Date',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      return <span>{row.original.revisionDate || '-'}</span>
    },
  },
]

interface SemanticSearchColumnsProps {
  feedbackSelectedIds: Set<string>
  onFeedbackToggle: (id: string) => void
}

export const SemanticSearchColumns = ({
  feedbackSelectedIds,
  onFeedbackToggle,
}: SemanticSearchColumnsProps): ColumnDef<DocumentTableRow>[] => [
  {
    id: 'rank',
    header: 'Rank',
    enableColumnFilter: false,
    cell: ({ row }) => {
      return <RankCell>{row.index + 1}</RankCell>
    },
  },
  ...DocumentTableColumns(),
  {
    id: 'feedback',
    header: 'Feedback',
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const isSelected = feedbackSelectedIds.has(row.original.id)
      const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        onFeedbackToggle(row.original.id)
      }
      return (
        <FeedbackButton
          data-selected={isSelected}
          onClick={handleClick}
          title={isSelected ? 'Remove from relevant' : 'Mark as relevant'}>
          {isSelected ? <LikeFilled /> : <LikeOutlined />}
        </FeedbackButton>
      )
    },
  },
]

export const transformDocumentsToTableData = (
  systemDocumentEntries: SystemDocumentEntry[]
): DocumentTableRow[] => {
  return systemDocumentEntries.map((entry) => {
    return {
      id: entry.entry.id,
      title: entry.entry.data.title,
      filename: entry.entry.data.filename || entry.entry.data.title,
      documentTitle: entry.entry.data.document_title || null,
      system: entry.entry.data.system || null,
      vendor: entry.entry.data.vendor_name || null,
      docType: entry.entry.data.document_type || null,
      drawing: entry.entry.data.drawing_number || null,
      revision: null,
      timestamp: entry.entry.data.uploadDate || null,
      numPages: entry.entry.data.total_pages || null,
      revisionCode: entry.entry.data.revision_code || null,
      revisionDate: entry.entry.data.revision_date || null,
    }
  })
}
